import { create } from 'zustand';

type FavoritesStorageModel = {
  taskIds: string[];
  broadcastIds: string[];
  isFavoritesHomeWidgetEnabled?: boolean;
};

type FavoritesPersistSlice = {
  favoriteTaskIds: Record<string, true>;
  favoriteBroadcastIds: Record<string, true>;
  isFavoritesHomeWidgetEnabled: boolean;
};

type FavoritesStore = {
  userId: string | null;
  favoriteTaskIds: Record<string, true>;
  favoriteBroadcastIds: Record<string, true>;
  isFavoritesHomeWidgetEnabled: boolean;

  setUserId: (userId: string | null) => void;
  replaceFavouritesFromServer: (
    taskIds: string[],
    broadcastIds: string[],
  ) => void;
  setTaskFavorite: (taskId: string, favorite: boolean) => void;
  setBroadcastFavorite: (broadcastId: string, favorite: boolean) => void;
  isTaskFavorite: (taskId: string) => boolean;
  isBroadcastFavorite: (broadcastId: string) => boolean;
  setFavoritesHomeWidgetEnabled: (enabled: boolean) => void;
};

const STORAGE_PREFIX = 'favorites';

const getStorageKey = (userId: string) => `${STORAGE_PREFIX}:${userId}`;

const parseFavorites = (value: string | null): FavoritesStorageModel | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as FavoritesStorageModel;
  } catch {
    return null;
  }
};

const toLookup = (ids: string[] = []) =>
  ids.reduce<Record<string, true>>((acc, id) => {
    acc[id] = true;

    return acc;
  }, {});

const persistModel = (userId: string, slice: FavoritesPersistSlice): void => {
  const model: FavoritesStorageModel = {
    taskIds: Object.keys(slice.favoriteTaskIds),
    broadcastIds: Object.keys(slice.favoriteBroadcastIds),
    isFavoritesHomeWidgetEnabled: slice.isFavoritesHomeWidgetEnabled,
  };

  localStorage.setItem(getStorageKey(userId), JSON.stringify(model));
};

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
  userId: null,
  favoriteTaskIds: {},
  favoriteBroadcastIds: {},
  isFavoritesHomeWidgetEnabled: false,

  setUserId: (userId) => {
    if (!userId) {
      set({
        userId: null,
        favoriteTaskIds: {},
        favoriteBroadcastIds: {},
        isFavoritesHomeWidgetEnabled: false,
      });

      return;
    }

    const model = parseFavorites(localStorage.getItem(getStorageKey(userId)));

    set({
      userId,
      favoriteTaskIds: toLookup(model?.taskIds),
      favoriteBroadcastIds: toLookup(model?.broadcastIds),
      isFavoritesHomeWidgetEnabled:
        model?.isFavoritesHomeWidgetEnabled ?? false,
    });
  },

  replaceFavouritesFromServer: (taskIds, broadcastIds) => {
    const { userId, isFavoritesHomeWidgetEnabled } = get();

    if (!userId) return;

    const favoriteTaskIds = toLookup(taskIds);
    const favoriteBroadcastIds = toLookup(broadcastIds);

    persistModel(userId, {
      favoriteTaskIds,
      favoriteBroadcastIds,
      isFavoritesHomeWidgetEnabled,
    });

    set({ favoriteTaskIds, favoriteBroadcastIds });
  },

  setTaskFavorite: (taskId, favorite) => {
    const { userId } = get();

    if (!userId) return;

    set((state) => {
      const next = { ...state.favoriteTaskIds };

      if (favorite) {
        next[taskId] = true;
      } else {
        delete next[taskId];
      }

      persistModel(userId, {
        favoriteTaskIds: next,
        favoriteBroadcastIds: state.favoriteBroadcastIds,
        isFavoritesHomeWidgetEnabled: state.isFavoritesHomeWidgetEnabled,
      });

      return { favoriteTaskIds: next };
    });
  },

  setBroadcastFavorite: (broadcastId, favorite) => {
    const { userId } = get();

    if (!userId) return;

    set((state) => {
      const next = { ...state.favoriteBroadcastIds };

      if (favorite) {
        next[broadcastId] = true;
      } else {
        delete next[broadcastId];
      }

      persistModel(userId, {
        favoriteTaskIds: state.favoriteTaskIds,
        favoriteBroadcastIds: next,
        isFavoritesHomeWidgetEnabled: state.isFavoritesHomeWidgetEnabled,
      });

      return { favoriteBroadcastIds: next };
    });
  },

  isTaskFavorite: (taskId) => !!get().favoriteTaskIds[taskId],
  isBroadcastFavorite: (broadcastId) =>
    !!get().favoriteBroadcastIds[broadcastId],

  setFavoritesHomeWidgetEnabled: (enabled) => {
    const { userId, favoriteTaskIds, favoriteBroadcastIds } = get();

    if (!userId) return;

    persistModel(userId, {
      favoriteTaskIds,
      favoriteBroadcastIds,
      isFavoritesHomeWidgetEnabled: enabled,
    });

    set({ isFavoritesHomeWidgetEnabled: enabled });
  },
}));
